import { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { fetcher } from 'services';
import type { ProductAuthoringActions, ProductSaveValues, SaveProductCommand } from './controller';
import type { EmbeddedOutletOrServiceCategory } from '../../outletOrServiceCategory/types';
import type { ProductAuthoringInput, ProductAuthoringPriceList, ProductAuthoringProduct } from './types';
import { withProductPrice } from './priceOverlay';

export type ProductAuthoringResponse<TProduct extends ProductAuthoringProduct = ProductAuthoringProduct> = {
  price_list: ProductAuthoringPriceList;
  outlets: ProductAuthoringInput<TProduct>['outlets'];
  products: TProduct[];
  prices: Record<string, string>;
};

type ProductAuthoringSaveResponse<TProduct extends ProductAuthoringProduct> = {
  product: TProduct;
  list_price: string | null;
};

function pricesWithOverlay<TProduct extends ProductAuthoringProduct>(
  response: ProductAuthoringResponse<TProduct>,
  productId: number,
  amount: string | null,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(withProductPrice(response.prices, productId, amount)).filter(([, value]) => value !== undefined),
  ) as Record<string, string>;
}

export type ProductAuthoringAdapter<TProduct extends ProductAuthoringProduct = ProductAuthoringProduct> = {
  input: ProductAuthoringInput<TProduct> | undefined;
  actions: ProductAuthoringActions<TProduct>;
  isLoading: boolean;
  error: unknown;
  /** Reconcile the read model from the server (e.g. after catalogue actions). */
  mutate: () => Promise<unknown>;
};

function authoringProductsUrl(vendorId: number, outlet: EmbeddedOutletOrServiceCategory): string {
  return outlet.vendor
    ? `supplier/vendors/${vendorId}/outlets/${outlet.id}/products/`
    : `supplier/service_categories/${outlet.id}/products/`;
}

/** Product detail URL hosting the catalogue action used by the drawer. */
export function authoringProductUrl(vendorId: number, product: ProductAuthoringProduct): string {
  return `${authoringProductsUrl(vendorId, product.outlet_or_service_category)}${product.id}/`;
}

export function productAuthoringInputFromResponse<TProduct extends ProductAuthoringProduct>(
  response: ProductAuthoringResponse<TProduct>,
): ProductAuthoringInput<TProduct> {
  return {
    priceList: response.price_list,
    outlets: response.outlets,
    products: response.products,
    prices: response.prices,
  };
}

/** Live read-model adapter for atomic product and list-price authoring. */
export function useProductAuthoring<TProduct extends ProductAuthoringProduct = ProductAuthoringProduct>(
  priceListEndpoint: string | null,
): ProductAuthoringAdapter<TProduct> {
  const { data, error, isLoading, mutate } = useSWR<ProductAuthoringResponse<TProduct>>(
    priceListEndpoint ? { url: `${priceListEndpoint}authoring/`, method: 'GET' } : null,
    fetcher,
  );

  const saveProduct = useCallback(
    async (command: SaveProductCommand<ProductSaveValues<TProduct>>) => {
      if (!data || !priceListEndpoint) throw new Error('Cannot save a product before the authoring data has loaded');
      const url = command.productId
        ? `${priceListEndpoint}authoring/products/${command.productId}/`
        : `${priceListEndpoint}authoring/products/`;
      const response = await fetcher<ProductAuthoringSaveResponse<TProduct>>({
        url,
        method: command.productId ? 'PUT' : 'POST',
        body: {
          product: command.values,
          ...(command.productId ? {} : { outlet_id: command.outletId }),
          ...(command.listPrice === undefined ? {} : { list_price: command.listPrice }),
        },
      });
      await mutate((current) => {
        if (!current) return current;
        const products = command.productId
          ? current.products.map((product) => (product.id === response.product.id ? response.product : product))
          : [...current.products, response.product];
        return {
          ...current,
          products,
          prices: pricesWithOverlay(current, response.product.id, response.list_price),
        };
      }, false);
      return response.product;
    },
    [data, mutate, priceListEndpoint],
  );

  const input = useMemo(() => (data ? productAuthoringInputFromResponse(data) : undefined), [data]);

  return {
    input,
    actions: { saveProduct },
    isLoading,
    error,
    mutate: () => mutate(),
  };
}
